import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import LinkIcon from "@mui/icons-material/Link";
import PaidIcon from "@mui/icons-material/Paid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DetalleTarjetaPedido from "./DetalleTarjetaPedido";
import { getColorEstado } from "../config/estadosPedido";
import {
  getAccionesPedido,
  getAccionPrincipal,
  getAccionesSecundarias,
} from "../utils/accionesPedido";
import { parseProductosPedido } from "../utils/productosPedido";

// El backend a veces manda "No"/"0" en vez de omitir el campo cuando el
// pedido no es para cambio de producto; sin este filtro el badge aparecía
// siempre.
const NEGATIVOS_CAMBIO = new Set(["", "0", "no", "false", "null"]);
const esCambioDeProducto = (valor) => {
  if (valor === null || valor === undefined) return false;
  return !NEGATIVOS_CAMBIO.has(String(valor).trim().toLowerCase());
};

// Estilo común para los badges que necesitan destacar sobre el resto de la
// tarjeta (cambio de producto, link de pago): relleno, en negrita, en su
// propia línea.
const CHIP_DESTACADO_SX = {
  height: "auto",
  minHeight: 20,
  fontSize: "0.68rem",
  fontWeight: 700,
  "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 },
};

// Tarjeta compacta: pensada para vivir dentro de una grilla (varias por
// fila), no como bloque de ancho completo. La sucursal no se repite acá
// porque ya la indica el encabezado del grupo que la contiene.
const TarjetaPedido = ({
  pedido,
  activeTab,
  abrirModal,
  modoSeleccion,
  seleccionado,
  onToggleSeleccion,
}) => {
  const [expandido, setExpandido] = useState(false);
  const [menuEl, setMenuEl] = useState(null);

  const { accent, chip } = getColorEstado(pedido.estado);
  const productos = parseProductosPedido(pedido);
  const acciones = getAccionesPedido(activeTab, pedido, abrirModal);
  const accionPrincipal = getAccionPrincipal(acciones);
  const accionesSecundarias = getAccionesSecundarias(acciones);

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: `3px solid ${accent}`,
        borderRadius: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          p: 1,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 1 },
        }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={0.5}>
          {modoSeleccion && activeTab === 0 && (
            <Checkbox
              size="small"
              checked={seleccionado}
              onChange={() => onToggleSeleccion(pedido.id)}
              sx={{ p: 0.25, mt: 0.1 }}
            />
          )}

          <Box
            sx={{ flexGrow: 1, minWidth: 0, cursor: "pointer" }}
            onClick={() => setExpandido((prev) => !prev)}
          >
            {/* Estado del pedido: siempre en su propia línea, arriba de todo. */}
            {pedido.valor && (
              <Box sx={{ mb: 0.25 }}>
                <Chip
                  size="small"
                  color={chip}
                  label={pedido.valor}
                  variant="filled"
                  sx={{
                    height: "auto",
                    minHeight: 20,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    "& .MuiChip-label": { whiteSpace: "normal", py: 0.15 },
                  }}
                />
              </Box>
            )}

            {/* Línea discreta: nº de pedido y cliente. Dato de referencia,
                no el foco de la tarjeta. */}
            <Typography variant="caption" color="text.primary">
              #{pedido.id} ·{" "}
              <Typography component="span" variant="body2" color="text.primary">
                {pedido.cliente}
              </Typography>
            </Typography>

            {/* Foco 1: dirección de entrega. */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
              sx={{ mt: 0.25 }}
            >
              <LocationOnIcon
                sx={{
                  fontSize: 15,
                  mt: "2px",
                  flexShrink: 0,
                  color: "text.secondary",
                }}
              />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ lineHeight: 1.3 }}
              >
                {[pedido.nombre_direccion, pedido.direccion_entrega]
                  .filter(Boolean)
                  .join(" - ")}{" "}
              </Typography>
            </Stack>

            {/* Foco 2: carga del pedido. Antes vivía dentro del detalle
                expandible; ahora se ve siempre, debajo de la dirección. */}
            {productos.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {productos.map((producto, index) => (
                  <Chip
                    key={index}
                    label={producto}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: "auto",
                      minHeight: 20,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.primary",
                      "& .MuiChip-label": { whiteSpace: "normal", py: 0.15 },
                    }}
                  />
                ))}
              </Stack>
            )}

            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <EventIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                {pedido.fecha_para}
              </Typography>
            </Stack>

            {(esCambioDeProducto(pedido.cambio) || pedido.link_pago === 1) && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {esCambioDeProducto(pedido.cambio) && (
                  <Chip
                    icon={
                      <SwapHorizIcon sx={{ fontSize: "14px !important" }} />
                    }
                    label={`Cambio de producto: ${pedido.cambio}`}
                    color="warning"
                    variant="filled"
                    sx={CHIP_DESTACADO_SX}
                  />
                )}
                {pedido.link_pago === 1 && pedido.pagado === 0 && (
                  <Chip
                    icon={<LinkIcon sx={{ fontSize: "14px !important" }} />}
                    label="Paga con link de pago"
                    color="info"
                    variant="filled"
                    sx={CHIP_DESTACADO_SX}
                  />
                )}
                {pedido.link_pago === 1 && pedido.pagado === 1 && (
                  <Chip
                    icon={<PaidIcon sx={{ fontSize: "14px !important" }} />}
                    label="Pagado con link de pago"
                    color="success"
                    variant="filled"
                    sx={CHIP_DESTACADO_SX}
                  />
                )}
              </Stack>
            )}
          </Box>

          <IconButton
            size="small"
            sx={{ p: 0.25 }}
            onClick={() => setExpandido((prev) => !prev)}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                transform: expandido ? "rotate(180deg)" : "none",
                transition: "transform 0.15s",
              }}
            />
          </IconButton>
        </Stack>

        <Collapse in={expandido}>
          <DetalleTarjetaPedido pedido={pedido} />
        </Collapse>

        {acciones.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: "auto", pt: 1 }}
            alignItems="center"
          >
            {accionPrincipal && (
              <Button
                size="small"
                variant="contained"
                color={
                  accionPrincipal.color === "action"
                    ? "inherit"
                    : accionPrincipal.color
                }
                disabled={accionPrincipal.disabled}
                startIcon={<accionPrincipal.icon sx={{ fontSize: 16 }} />}
                onClick={accionPrincipal.onClick}
                sx={{ flexGrow: 1, fontSize: "0.75rem", py: 0.25 }}
              >
                {accionPrincipal.label}
              </Button>
            )}
            {accionesSecundarias.length > 0 && (
              <>
                <IconButton
                  size="small"
                  sx={{ p: 0.5 }}
                  onClick={(e) => setMenuEl(e.currentTarget)}
                >
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Menu
                  anchorEl={menuEl}
                  open={Boolean(menuEl)}
                  onClose={() => setMenuEl(null)}
                >
                  {accionesSecundarias.map((accion) => (
                    <MenuItem
                      key={accion.key}
                      disabled={accion.disabled}
                      onClick={() => {
                        setMenuEl(null);
                        accion.onClick();
                      }}
                    >
                      <ListItemIcon>
                        <accion.icon
                          fontSize="small"
                          color={
                            accion.color === "action" ? "inherit" : accion.color
                          }
                        />
                      </ListItemIcon>
                      <ListItemText>{accion.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default TarjetaPedido;
